import { Select, SelectProps } from '@chakra-ui/react'

import { ISelectOption, SelectValue } from './types'

export interface IFormSelectProps extends Omit<
  SelectProps,
  'onChange' | 'value' | 'children' | 'placeholder' | 'defaultValue'
> {
  /** Valor atual. `emptyValue` (por padrão `''`) quer dizer "nada escolhido". */
  value: SelectValue
  /** Recebe sempre a string do `<option>` — converta na página se o campo for numérico. */
  onChange: (value: string) => void
  options: ISelectOption[]
  /**
   * Rótulo da opção vazia. `Selecione` é o padrão do painel; passe algo mais específico só quando
   * a frase informar de verdade ("Selecione o formato primeiro").
   */
  placeholder?: string
  /** Valor da opção vazia, para os formulários que usam `0` (e não `''`) como "nada escolhido". */
  emptyValue?: SelectValue
  /** Tira a opção vazia, para o campo que já nasce com um valor e não aceita voltar a vazio. */
  hideEmptyOption?: boolean
  /** As opções ainda estão sendo buscadas: o campo fica desabilitado e diz isso. */
  isLoading?: boolean
}

/**
 * Select nativo do painel — a irmã do `<FilterSelect>` para dentro do formulário.
 *
 * A página declara as opções, não as `<option>`: a opção vazia é do componente, e por isso ela tem
 * uma grafia só. Havia onze (`Selecione`, `Selecione uma opção`, `Selecione...`, `Selecione um
 * concurso...`, além das que eram `<option value="0">` e das que vinham pela prop `placeholder` do
 * Chakra, que renderiza uma `<option value="">` — duas mecânicas para a mesma linha).
 *
 * ```tsx
 * <FormField label="Grupo do usuário" error={errors.user_group_id} isRequired>
 *   <FormSelect
 *     value={form.user_group_id}
 *     onChange={(value) => setForm({ ...form, user_group_id: Number(value) })}
 *     options={userGroups.map((group) => ({ value: group.id, label: group.name }))}
 *     emptyValue={0}
 *   />
 * </FormField>
 * ```
 *
 * Use este para lista curta e fixa. Para lista longa o bastante para valer digitar (eventos,
 * concursos, cursos, professores) ou para escolha múltipla, use `<SearchableSelect>`.
 *
 * **Não passe `size`**: a altura é `md`, como todo campo de formulário. A exceção é o formulário de
 * linha repetida (`TicketsTab`, `ArtFieldsEditor`), onde `sm` é a altura de todos os campos da
 * linha — a mesma exceção que o `AGENTS.md` já abre para os demais controles.
 */
export default function FormSelect({
  value,
  onChange,
  options,
  placeholder = 'Selecione',
  emptyValue = '',
  hideEmptyOption = false,
  isLoading = false,
  isDisabled,
  ...rest
}: IFormSelectProps) {
  return (
    <Select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      isDisabled={isLoading || (isDisabled ?? false)}
      {...rest}
    >
      {!hideEmptyOption && <option value={emptyValue}>{isLoading ? 'Carregando...' : placeholder}</option>}

      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.isDisabled}>
          {option.label}
        </option>
      ))}
    </Select>
  )
}
